import { useLayoutEffect, useRef } from "react";
import datetime from "config/datetime";
import { DataVisualizationType } from "components/charting/config/chartTypes";
import { useChartSetSelectContext } from "components/charting/contexts/ChartSetSelectContext";
import { useChartSetSelectStatusContext } from "components/charting/contexts/ChartSetSelectStatusContext";
import { useChartProfileSelectContext } from "components/charting/contexts/ChartProfileSelectContext";
import { useChartProfileSelectStatusContext } from "components/charting/contexts/ChartProfileSelectStatusContext";
import useChartSelections from "components/charting/hooks/useChartSelections";
import useDataVisualizationType from "components/charting/hooks/useDataVisualizationType";
import useProfileTypeQueryState from "components/charting/hooks/useProfileTypeQueryState";
import type { MeasurementSet } from "types";

/**
 * Every set from the newest day. Only one side is plotted, the newest set's, so unilateral sets are never mixed.
 */
function getDefaultSets(newestFirstSets: MeasurementSet[]) {
  const { completedDate, variant } = newestFirstSets[0];
  const newestDay = datetime(completedDate);
  return newestFirstSets.filter(
    (set) => set.variant === variant && datetime(set.completedDate).isSame(newestDay, "day")
  );
}

/**
 * Once an athlete's exercise loads, plots its profile (the first side when unilateral) or its latest sets.
 * The profile is plotted again on returning to its profile type, unless something else was picked meanwhile.
 */
function useSyncChartSetSelectionDefaults() {
  const selections = useChartSelections();
  const visType = useDataVisualizationType();
  const { value: sets, options: setOptions, onChange: onSetsChange } = useChartSetSelectContext();
  const { value: profiles, options: profileOptions, onChange: onProfilesChange } = useChartProfileSelectContext();
  const setStatus = useChartSetSelectStatusContext();
  const profileStatus = useChartProfileSelectStatusContext();
  const [profileType] = useProfileTypeQueryState();

  const loadKey = `${selections.sources[0]?.id}-${selections.measures[0]?.id}`;
  const defaultKey = `${loadKey}-${profileType}`;
  const defaultedLoadKey = useRef<string>();
  const defaultedKey = useRef<string>();

  // A failed request is treated as having no options, so whatever did load can still be plotted.
  const isLoaded = setStatus !== "fetching" && profileStatus !== "fetching";

  // A layout effect, so the chart never paints empty between the options loading and the default being selected.
  useLayoutEffect(() => {
    if (visType !== DataVisualizationType.PROFILING_CHART || !isLoaded) return;
    if (defaultedKey.current === defaultKey) return;
    // Nothing to plot yet, but the previous profile type no longer counts as defaulted.
    if (!setOptions.length && !profileOptions.length) {
      defaultedKey.current = undefined;
      return;
    }

    const isNewLoad = defaultedLoadKey.current !== loadKey;
    defaultedKey.current = defaultKey;
    defaultedLoadKey.current = loadKey;
    if (sets.length || profiles.length) return;

    if (profileOptions.length) {
      onProfilesChange([profileOptions[0]]);
    } else if (isNewLoad) {
      onSetsChange(getDefaultSets(setOptions));
    }
  }, [
    defaultKey,
    isLoaded,
    loadKey,
    onProfilesChange,
    onSetsChange,
    profileOptions,
    profiles.length,
    sets.length,
    setOptions,
    visType,
  ]);
}

export default useSyncChartSetSelectionDefaults;
